import { Phone, MessageCircle, Mail, Send, ArrowUpRight } from "lucide-react";
import { useLang, useL } from "@/context/LanguageContext";
import { CONTACT, WHATSAPP_ENABLED, PHONE_ENABLED } from "@/data/copy";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { Link } from "@/components/site/LocaleLink";

export const SupportCTA = () => {
  const { t, isRtl } = useLang();
  const L = useL();
  const waMsg = isRtl ? "مرحباً، أحتاج دعماً فنياً." : "Hello, I need technical support.";

  const channels = [
    {
      icon: Mail,
      label: t.supportCta.email,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      testid: "support-email",
    },
    {
      icon: Send,
      label: t.supportCta.form,
      value: t.supportCta.formValue,
      href: "/contact",
      testid: "support-form",
      internal: true,
    },
  ];

  if (PHONE_ENABLED) {
    channels.unshift({
      icon: Phone,
      label: t.supportCta.call,
      value: CONTACT.phone,
      href: `tel:${CONTACT.phoneHref}`,
      testid: "support-call",
    });
  }

  if (WHATSAPP_ENABLED) {
    channels.splice(PHONE_ENABLED ? 1 : 0, 0, {
      icon: MessageCircle,
      label: t.supportCta.whatsapp,
      value: CONTACT.phone,
      href: `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(waMsg)}`,
      testid: "support-whatsapp",
      accent: true,
    });
  }

  return (
    <section className="relative overflow-hidden bg-forest-deep py-24 text-cream lg:py-28" data-testid="support-cta">
      <div className="noise pointer-events-none absolute inset-0 opacity-[0.05]" />
      <div className="container-x relative grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <div>
          <Reveal>
            <p className="eyebrow mb-4 text-gold-light">{t.supportCta.kicker}</p>
          </Reveal>
          <Reveal i={1}>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              {t.supportCta.title}
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/75 sm:text-lg">{t.supportCta.sub}</p>
          </Reveal>
          <Reveal i={3}>
            <div className="mt-8 flex flex-wrap gap-2">
              {t.supportCta.inquiryTypes.map((tag, i) => (
                <span key={i} className="rounded-full border border-cream/20 px-4 py-1.5 text-xs text-cream/70">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <RevealGroup className="flex flex-col gap-4">
          {channels.map((ch) => {
            const Icon = ch.icon;
            const cardClass = `group flex items-center justify-between rounded-xl border p-5 transition-all duration-300 hover:-translate-y-0.5 ${
              ch.accent
                ? "border-transparent bg-[#25D366] text-white"
                : "border-cream/15 bg-cream/[0.04] hover:border-gold-light/40"
            }`;
            const Wrapper = ch.internal ? Link : "a";
            const wrapperProps = ch.internal
              ? { to: ch.href }
              : {
                  href: ch.href,
                  target: ch.href.startsWith("http") ? "_blank" : undefined,
                  rel: "noopener noreferrer",
                };
            return (
              <RevealItem key={ch.testid}>
                <Wrapper {...wrapperProps} data-testid={ch.testid} className={cardClass}>
                  <span className="flex items-center gap-4">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                        ch.accent ? "bg-white/20" : "bg-cream/10 text-gold-light"
                      }`}
                    >
                      <Icon className="h-6 w-6" strokeWidth={1.8} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-wider opacity-70">{ch.label}</span>
                      <span dir="ltr" className="block font-display text-lg font-bold">
                        {ch.value}
                      </span>
                    </span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 opacity-70 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 rtl-flip" />
                </Wrapper>
              </RevealItem>
            );
          })}
        </RevealGroup>
      </div>
    </section>
  );
};
